import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FormProvider from '../form/FormProvider';
import TextField from '../form/TextField';
import PhoneField from '../form/PhoneField';
import SelectField from '../form/SelectField';
import { Roles } from '../../constants';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Invalid email'),
  nmls: z.string().optional().default(''),
  phone: z.string().optional().default(''),
  vestaContactId: z.string().optional().default(''),
  role: z.string().min(1, 'Role is required'),
});

const BROKER_ROLE_OPTIONS = [
  { value: Roles.Broker, label: 'Loan Officer' },
  { value: Roles.Processor, label: 'Processor' },
  { value: Roles.BrokerFullAccessReadOnly, label: 'Read-only (full pipeline & loans)' },
];

const LENDER_ROLE_OPTIONS = [
  { value: Roles.BrokerAdmin, label: 'Broker Manager' },
  { value: Roles.Broker, label: 'Loan Officer' },
  { value: Roles.Processor, label: 'Processor' },
  { value: Roles.BrokerFullAccessReadOnly, label: 'Read-only (full pipeline & loans)' },
];

/**
 * Modal for editing a user's details.
 */
const EditUserModal = ({ user, canEditNmls = true, mode = 'lender', users = [], onSave, onClose }) => {
  const isBrokerMode = mode === 'broker';
  const hasBrokerAdmin = users.some(u => u.role === Roles.BrokerAdmin && u.userId !== user.userId);
  // BrokerAdmin editing themselves cannot change their own role (would orphan the company)
  const isEditingSelfAsBrokerAdmin = isBrokerMode && user.role === Roles.BrokerAdmin;
  const roleOptions = isBrokerMode
    ? BROKER_ROLE_OPTIONS
    : LENDER_ROLE_OPTIONS.map(opt => opt.value === Roles.BrokerAdmin && hasBrokerAdmin
        ? { ...opt, label: `${opt.label} (already assigned)`, disabled: true }
        : opt
      );
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: user.firstName || '', lastName: user.lastName || '',
      emailAddress: user.emailAddress || '', nmls: user.nmls || '',
      phone: user.phone || '', vestaContactId: user.vestaContactId || '',
      role: user.role || '',
    },
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const vestaContactId = methods.watch('vestaContactId');

  const handleSubmit = async (data) => {
    setError('');
    setSubmitting(true);
    try {
      await onSave({ userId: user.userId, ...data, vestaContactId: data.vestaContactId?.trim() || null });
      onClose();
    } catch (err) {
      const msg = err.data?.title
        || err.data?.[0]?.description
        || (Array.isArray(err.data) ? err.data.map(e => e.description).join(', ') : null)
        || 'Failed to save user';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}><TextField name="firstName" label="First Name" required size="small" /></Grid>
            <Grid size={{ xs: 6 }}><TextField name="lastName" label="Last Name" required size="small" /></Grid>
          </Grid>
          <TextField name="emailAddress" label="Email" type="email" required size="small" sx={{ mb: 2 }} />
          <TextField name="nmls" label="NMLS" size="small" disabled={!canEditNmls} helperText={!canEditNmls ? 'NMLS can only be changed by a Lender Admin' : undefined} sx={{ mb: 2 }} />
          <PhoneField name="phone" label="Phone" size="small" sx={{ mb: 2 }} />
          <SelectField name="role" label="Role" options={roleOptions} required size="small" disabled={isEditingSelfAsBrokerAdmin} helperText={isEditingSelfAsBrokerAdmin ? 'Broker Managers cannot change their own role' : undefined} sx={{ mb: 2 }} />
          {canEditNmls && (
            <TextField
              name="vestaContactId"
              label="UFF Contact ID"
              size="small"
              placeholder="e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890"
              sx={{
                '& .MuiInputBase-input': { fontFamily: 'monospace' },
                ...(!vestaContactId ? { '& .MuiOutlinedInput-root': { borderColor: 'warning.main', bgcolor: 'warning.lighter' } } : {}),
              }}
              helperText={
                vestaContactId
                  ? <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />Linked to UFF</Typography>
                  : <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />Not linked — user cannot create loans until linked</Typography>
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={16} /> : <SaveIcon />}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default EditUserModal;
